import type { FC } from 'react'

import { ChevronDownOutline16, ChevronUpOutline16 } from '@sok/design-system'
import { SkeletonLoader } from 'components/SkeletonLoader'
import React, { useState } from 'react'
import styled from 'styled-components'

export const StoreInfoCard = styled.div({
  position: 'relative',
})

export const StoreInfoCardTitle = styled.h3(({ theme }) => ({
  ...theme.variants.paragraph2.regular,
  paddingBottom: theme.spacings.medium,
}))

export const StoreInfoCardItemWrapper = styled.div(({ theme }) => ({
  ...theme.variants.body2.regular,
  padding: `${theme.spacings.small} ${theme.spacings.medium}`,

  borderTop: `1px solid ${theme.colors.grey200}`,
  borderBottom: `1px solid ${theme.colors.grey200}`,

  marginTop: '-1px',

  display: 'flex',
  flexDirection: 'column',

  ['&:last-child']: {
    borderBottom: `1px solid ${theme.colors.grey200}`,
  },

  ['> * + *']: {
    marginTop: theme.spacings.xSmall,
  },
}))

export const StoreInfoCardItem = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  ['> * + *']: {
    marginLeft: theme.spacings.medium,
  },
}))

export const StoreInfoCardItemDescription = styled.div({
  padding: '0 0 15px 15px',
})

const StoreInfoCardButton = styled(StoreInfoCardItem).attrs({ as: 'button' })(({ theme }) => ({
  ...theme.variants.body2.regular,
  background: 'none',
  border: 'none',
  cursor: 'pointer',

  ['> .icon:last-child']: {
    marginLeft: 'auto',
  },
}))

const ArrowIconSkeletonLoader = styled(SkeletonLoader)({})

const ExpandableContent = styled.div<{ isOpen: boolean }>(({ isOpen, theme }) => ({
  display: isOpen ? 'block' : 'none',

  ['> * + *']: {
    marginTop: theme.spacings.xSmall,
  },
}))

const _StoreInfoCardSkeletonLoader: FC<{ className?: string }> = ({ className }) => (
  <StoreInfoCard className={className}>
    <StoreInfoCardItemWrapper>
      <SkeletonLoader width="88px" />
      <SkeletonLoader />
      <StoreInfoCardItem>
        <SkeletonLoader width="24px" />
        <SkeletonLoader />
      </StoreInfoCardItem>
      <StoreInfoCardItem>
        <SkeletonLoader width="24px" />
        <SkeletonLoader width="232px" />
        <SkeletonLoader width="24px" />
      </StoreInfoCardItem>
    </StoreInfoCardItemWrapper>

    <ArrowIconSkeletonLoader width="24px" />
  </StoreInfoCard>
)

export const StoreInfoCardSkeletonLoader = styled(_StoreInfoCardSkeletonLoader)(({ theme }) => ({
  [SkeletonLoader]: {
    height: 24,
  },

  [ArrowIconSkeletonLoader]: {
    position: 'absolute',
    right: '0',
    top: '50%',
    transform: 'translateY(-50%)',
  },

  [StoreInfoCardItemWrapper]: {
    paddingRight: theme.spacings.xLarge,
  },
}))

export const StoreInfoCardItemExpandable: FC<{
  icon: React.ReactNode
  title: string | React.ReactNode
  children: React.ReactNode
}> = ({ icon, title, children }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <StoreInfoCardItemWrapper>
      <StoreInfoCardButton onClick={() => setIsOpen((state) => !state)}>
        {icon}
        <span>{title}</span>
        {isOpen ? <ChevronUpOutline16 /> : <ChevronDownOutline16 />}
      </StoreInfoCardButton>
      <ExpandableContent isOpen={isOpen}>{children}</ExpandableContent>
    </StoreInfoCardItemWrapper>
  )
}
